/**
 * Clasificación de errores de la API, compartida por las colas offline.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO EN CADA API: las dos colas (asistencia y canjes) necesitan la misma
 * decisión —"¿esto se reintenta o el servidor ya dijo que no?"— y esa decisión depende de una
 * lista de códigos. Una lista copiada en dos archivos se desincroniza en cuanto aparece un
 * código nuevo, y el síntoma sería silencioso: un rechazo definitivo reintentándose para
 * siempre, o un fallo de red gastando los intentos de alguien que solo estaba sin señal. Es el
 * mismo error que ya se cometió con el diccionario de banderas copiado en tres pantallas
 * (BITACORA §10).
 */

/**
 * Error de la API conservando el código que lo originó: `code` de PostgREST/Postgres
 * (p. ej. `42501`) o `statusCode` HTTP del Storage (p. ej. `403`). Sin ese código, una cola
 * offline no puede distinguir "no hay señal" de "el servidor lo rechazó".
 */
export class ApiErrorConCodigo extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null = null, name = 'ApiErrorConCodigo') {
    super(message);
    this.name = name;
    this.code = code;
  }
}

/** Rechazos de Postgres que NO se arreglan reintentando. */
const PERMANENT_PG_CODES = new Set(['42501', '23502', '23503', '23514', '22P02']);
/** 4xx que sí conviene reintentar. */
const RETRYABLE_HTTP_CODES = new Set(['408', '429']);
/** Violación de unicidad: para una cola idempotente es éxito, no rechazo. */
const DUPLICATE_PG_CODE = '23505';

/**
 * true si el servidor rechazó la operación de forma DEFINITIVA. Un fallo de red (sin código),
 * un 5xx o un duplicado devuelven false: eso se reintenta, o ya es éxito.
 */
export function esErrorPermanente(err: unknown): boolean {
  if (!(err instanceof ApiErrorConCodigo) || err.code === null) return false;
  if (err.code === DUPLICATE_PG_CODE) return false;
  if (/^4\d\d$/.test(err.code)) return !RETRYABLE_HTTP_CODES.has(err.code);
  return PERMANENT_PG_CODES.has(err.code);
}

export { DUPLICATE_PG_CODE };
